/**
 * TRTF sticky add to cart (mobile)
 * ------------------------------------------------------------------
 * Most of TRTF traffic arrives from Instagram and TikTok on a phone, where
 * the real add-to-cart button is off screen for most of the product page.
 * This puts a persistent one at the foot of the screen from the moment the
 * real one scrolls away.
 *
 * It deliberately does NOT contain its own form. A second form would be a
 * second source of truth for variant, quantity and selling plan, and the two
 * would drift. Instead the sticky button forwards the click to the real
 * add-to-cart button, so whatever the customer picked is what gets added,
 * and the theme own validation, loading state and cart drawer still fire.
 *
 * Price and availability mirror the real ones, so a variant change updates
 * both without this file knowing anything about variants.
 */
(function () {
  const MOBILE_QUERY = '(max-width: 749px)';

  class TrtfStickyAtc extends HTMLElement {
    connectedCallback() {
      this.mobile = window.matchMedia(MOBILE_QUERY);
      this.button = this.querySelector('[data-sticky-add]');
      this.priceEl = this.querySelector('[data-sticky-price]');

      this.findAnchors();
      if (!this.mainButton) return;

      this.button.addEventListener('click', () => this.forwardClick());

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Only show once the real button has scrolled ABOVE the viewport.
            // Showing it while the real button is still below would put two
            // add-to-carts on screen at once.
            const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', passed && this.mobile.matches);
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(this.mainButton);

      this.mobile.addEventListener('change', () => {
        if (!this.mobile.matches) this.classList.remove('is-visible');
      });

      this.watchState();
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.stateObserver) this.stateObserver.disconnect();
    }

    findAnchors() {
      const form =
        document.querySelector('product-form form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      this.mainButton =
        form.querySelector('[name="add"]') ||
        form.querySelector('.product-form__submit');
      this.mainPrice = document.querySelector(
        '.product__info-container .price, .product .price'
      );
    }

    forwardClick() {
      if (!this.mainButton || this.mainButton.disabled) return;
      this.mainButton.click();
    }

    /**
     * Mirror the real button disabled state and the real price, so a variant
     * change or a sell-out is reflected here without duplicating any logic.
     */
    watchState() {
      const sync = () => {
        if (this.mainButton.disabled) {
          this.button.setAttribute('disabled', '');
        } else {
          this.button.removeAttribute('disabled');
        }
        const label = this.mainButton.querySelector('span, .button-label');
        if (label && label.textContent.trim()) {
          this.button.textContent = label.textContent.trim();
        }
        if (this.mainPrice && this.priceEl) {
          const markup = this.mainPrice.outerHTML;
          if (markup && this._lastPrice !== markup) {
            this._lastPrice = markup;
            this.priceEl.innerHTML = markup;
          }                   
        }
      };

      sync();
      this.stateObserver = new MutationObserver(sync);
      this.stateObserver.observe(this.mainButton, {
        attributes: true,
        childList: true,
        subtree: true,
      });
      if (this.mainPrice) {
        this.stateObserver.observe(this.mainPrice, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    }
  }

  if (!customElements.get('trtf-sticky-atc')) {
    customElements.define('trtf-sticky-atc', TrtfStickyAtc);
  }
})();
